import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useWindowSize } from "../../../../../Helpers";
import Button from "@components/Button";
import "./style.scss";
import LinearGradientProgressBar from "@components/LinearGradientProgressBar";
import StickyButton from "../../../../../components/StickyButton";
import PagesHeader from "@components/PagesHeader";
import StickyContent from "@components/StickyContent";
import BrandLogo from "@components/BrandLogo";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";

const ERASER_WIDTH = 20;
const ERASER_THRESHOLD = 90;

export default function Scratch({
  currentQuestion,
  setCurrentQuestion,
  setAnswers,
  answers,
  sendAnswers,
}) {
  const { ident } = useParams();
  const { width } = useWindowSize();
  const queryClient = useQueryClient();
  const gameQuestionsQuery = queryClient.getQueryData([
    "game-questions",
    ident,
  ]);
  const XGA = width >= 1020;

  const prevDrawCoordinates = useRef([null, null]);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchedPercentage, setScratchedSPercentage] = useState(0);
  const [hint, setHint] = useState(false);

  const drawDot = (x, y) => {
    ctxRef.current.globalCompositeOperation = "destination-out";

    if (prevDrawCoordinates.current.includes(null)) {
      ctxRef.current.beginPath();
      ctxRef.current.arc(x, y, ERASER_WIDTH, 0, 2 * Math.PI, true);
      // ctxRef.current.fillStyle = '#000';
      ctxRef.current.fill();
      prevDrawCoordinates.current = [x, y];
      return;
    }

    ctxRef.current.beginPath();
    ctxRef.current.moveTo(
      prevDrawCoordinates.current[0],
      prevDrawCoordinates.current[1],
    );
    ctxRef.current.lineTo(x, y);
    ctxRef.current.lineWidth = ERASER_WIDTH * 2;
    ctxRef.current.lineCap = "round";
    ctxRef.current.stroke();
    prevDrawCoordinates.current = [x, y];
  };

  const getPercentage = () => {
    let pixelCount = canvasRef.current.width * canvasRef.current.height;
    let arrayElemsCount = pixelCount * 4;
    let dataArray = ctxRef.current.getImageData(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height,
    ).data;
    let threshold = 0.5;
    let transparentPixelCount = 0;
    for (let i = 3; i < arrayElemsCount; i += 4) {
      let alphaValue = dataArray[i];
      if (alphaValue <= threshold) {
        transparentPixelCount++;
      }
    }
    setScratchedSPercentage((transparentPixelCount / pixelCount) * 100);
  };

  useEffect(() => {
    if (scratchedPercentage >= 80) {
      setHint(true);
    }
  }, [scratchedPercentage]);

  const startScratch = ({ nativeEvent }) => {
    // if (selected === -1) {
    //   return
    // }
    setIsScratching(true);
    const { offsetX, offsetY } = nativeEvent;
    drawDot(offsetX, offsetY);
  };

  const stopScratch = () => {
    setIsScratching(false);
    getPercentage();
    prevDrawCoordinates.current = [null, null];
  };

  const scratch = ({ nativeEvent }) => {
    if (!isScratching) {
      return;
    }
    const { offsetX, offsetY } = nativeEvent;
    drawDot(offsetX, offsetY);
  };

  const startScratchMobile = ({ nativeEvent }) => {
    // if (selected === -1) {
    // return
    // }
    setIsScratching(true);
    const { clientX, clientY } = nativeEvent.targetTouches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    drawDot(clientX - rect.x, clientY - rect.y);
  };

  const scratchMobile = ({ nativeEvent }) => {
    if (!isScratching) {
      return;
    }
    const { clientX, clientY } = nativeEvent.targetTouches[0];
    const rect = canvasRef.current.getBoundingClientRect();
    drawDot(clientX - rect.x, clientY - rect.y);
  };

  const resetCanvas = () => {
    const img = new Image();
    img.onload = () => {
      ctxRef.current.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
      ctxRef.current.globalCompositeOperation = "source-over";
      ctxRef.current.drawImage(
        img,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
    };
    img.src =
      gameQuestionsQuery?.content[currentQuestion][
        width < 1020 ? "placeholderImgMobile" : "placeholderImgDesktop"
      ];
    setScratchedSPercentage(0);
  };

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctxRef.current = ctx;
    resetCanvas();
  }, [canvasRef.current, currentQuestion]);

  if (!gameQuestionsQuery?.content) {
    return;
  }

  function nextQuestion() {
    setHint(false);
    setScratchedSPercentage(0);
    setAnswers((prev) => [
      ...prev,
      {
        question_ident: `q${currentQuestion}`,
        answer_ident: gameQuestionsQuery?.content[currentQuestion].setAnswer,
      },
    ]);
    setCurrentQuestion((prev) =>
      prev <= gameQuestionsQuery.content.length - 1 ? ++prev : prev,
    );
  }

  function fitToContainer() {
    canvasRef.current.style.width =
      gameQuestionsQuery?.content[currentQuestion].width;
    canvasRef.current.style.height =
      gameQuestionsQuery?.content[currentQuestion].height;
    canvasRef.current.width = canvasRef.current.offsetWidth;
    canvasRef.current.height = canvasRef.current.offsetHeight;
  }

  useEffect(() => {
    fitToContainer();
  }, []);

  useEffect(() => {
    if (hint && gameQuestionsQuery?.content[currentQuestion]["modal"]) {
      NiceModal.show("correct-answer-modal", {
        header: gameQuestionsQuery?.content[currentQuestion]["modalHeader"],
        content: gameQuestionsQuery?.content[currentQuestion]["modalContent"],
        img: gameQuestionsQuery?.content[currentQuestion]["modalImg"],
      });
    }
  }, [hint]);

  const imgVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
    },
    out: {
      opacity: 0,
    },
  };

  const imgTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.0,
  };

  return (
    <section className={`game-page scratch-mechanics ${ident}`}>
      <div>
        <PagesHeader back>
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(gameQuestionsQuery?.title),
            }}
          ></p>
        </PagesHeader>
        <div className="main-game-wrppaer">
          <div className="desktop-wrapper">
            <LinearGradientProgressBar
              value={currentQuestion / gameQuestionsQuery.content.length}
            />
            <div className="game-question">
              <h2
                className="question"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].question),
                }}
              />
              {hint && gameQuestionsQuery.content[currentQuestion].answer && (
                <h2
                  className="answerScratch"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(gameQuestionsQuery.content[currentQuestion].answer),
                  }}
                />
              )}
            </div>
          </div>
          <div className="side-img">
            <BrandLogo brands={gameQuestionsQuery?.brands} />

            <div
              className={`canvas-wrapper ${ident}`}
              style={{
                backgroundImage: `url(${
                  gameQuestionsQuery?.content[currentQuestion][
                    width < 1020 ? "correctImgMobile" : "correctImgDesktop"
                  ]
                })`,
              }}
            >
              <motion.canvas
                key={currentQuestion}
                initial="initial"
                animate="in"
                variants={imgVariants}
                transition={imgTransition}
                onAnimationStart={() => {
                  fitToContainer();
                }}
                width={gameQuestionsQuery?.content[currentQuestion].width}
                height={gameQuestionsQuery?.content[currentQuestion].height}
                ref={canvasRef}
                onMouseDown={startScratch}
                onTouchStart={startScratchMobile}
                onMouseUp={stopScratch}
                onTouchEnd={stopScratch}
                onMouseMove={scratch}
                onTouchMove={scratchMobile}
              />
            </div>
            {XGA && (
              // replace "StickyContent" with something called respectively
              <StickyContent>
                <div className="desktop-buttons">
                  <Button
                    disabled={scratchedPercentage <= 80}
                    action={nextQuestion}
                  >
                    {gameQuestionsQuery.content[currentQuestion]
                      .nextButtonCopy ?? "Nächste"}
                  </Button>
                </div>
              </StickyContent>
            )}
          </div>
        </div>
      </div>
      {!XGA && (
        // replace with "StickyContent"
        <StickyButton
          disabled={scratchedPercentage <= 80}
          action={nextQuestion}
        >
          {gameQuestionsQuery.content[currentQuestion].nextButtonCopy ??
            "Nächste"}
        </StickyButton>
      )}
    </section>
  );
}
